import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Users, Search, Filter, Mail, Phone, ShoppingBag, Calendar,
  TrendingUp, Star, ChevronRight, Download, ArrowUpDown, Eye,
  Package, CreditCard, MapPin, Clock, MoreVertical, UserPlus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BusinessCustomersPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('total_spent'); // total_spent, orders_count, last_order
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, [sortBy]);

  const fetchCustomers = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/business/register');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/business/customers?sort_by=${sortBy}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setCustomers(data.customers || []);
        setStats(data.stats || null);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(customer => 
    customer.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.phone?.includes(searchQuery)
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Jamais';
    return new Date(dateStr).toLocaleDateString('fr-FR', { 
      day: 'numeric', month: 'short', year: 'numeric' 
    });
  };

  const getCustomerBadge = (customer) => {
    if (!customer) return null;
    const spent = customer.total_spent || 0;
    if (spent >= 500) return { label: 'VIP', color: 'bg-gradient-to-r from-amber-400 to-yellow-500 text-white' };
    if (spent >= 200) return { label: 'Fidèle', color: 'bg-purple-100 text-purple-700' };
    if (customer.orders_count >= 3) return { label: 'Régulier', color: 'bg-blue-100 text-blue-700' };
    return null;
  };

  const viewCustomerDetail = (customer) => {
    setSelectedCustomer(customer);
    setShowDetailDialog(true);
  };

  const exportCustomers = () => {
    // Create CSV
    const headers = ['Nom', 'Email', 'Téléphone', 'Commandes', 'Total dépensé', 'Dernière commande'];
    const rows = customers.map(c => [
      c.name, c.email, c.phone || '', c.orders_count, c.total_spent, c.last_order_date || ''
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clients_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Export téléchargé !');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-charcoal flex items-center gap-2">
                <Users className="w-6 h-6 text-amber-500" />
                Fichier Clients
              </h1>
              <p className="text-sm text-muted-foreground">
                {customers.length} client{customers.length > 1 ? 's' : ''} enregistré{customers.length > 1 ? 's' : ''}
              </p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={exportCustomers}
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Exporter CSV
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-blue-600 mb-1">
                  <Users className="w-4 h-4" />
                  <span className="text-xs font-medium">Total clients</span>
                </div>
                <p className="text-2xl font-bold text-blue-700">{stats.total_customers}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-green-600 mb-1">
                  <CreditCard className="w-4 h-4" />
                  <span className="text-xs font-medium">CA total</span>
                </div>
                <p className="text-2xl font-bold text-green-700">{formatCurrency(stats.total_revenue)}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-purple-600 mb-1">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-xs font-medium">Panier moyen</span>
                </div>
                <p className="text-2xl font-bold text-purple-700">{formatCurrency(stats.average_order)}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-amber-600 mb-1">
                  <Star className="w-4 h-4" />
                  <span className="text-xs font-medium">Clients VIP</span>
                </div>
                <p className="text-2xl font-bold text-amber-700">{stats.vip_count}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher un client..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            <Button 
              variant={sortBy === 'total_spent' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setSortBy('total_spent')}
              className="text-xs"
            >
              <CreditCard className="w-3 h-3 mr-1" />
              CA
            </Button>
            <Button 
              variant={sortBy === 'orders_count' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setSortBy('orders_count')}
              className="text-xs"
            >
              <ShoppingBag className="w-3 h-3 mr-1" />
              Commandes
            </Button>
            <Button 
              variant={sortBy === 'last_order' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setSortBy('last_order')}
              className="text-xs"
            >
              <Clock className="w-3 h-3 mr-1" />
              Récent
            </Button>
          </div>
        </div>

        {/* Customers List */}
        {filteredCustomers.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">Aucun client</h3>
              <p className="text-muted-foreground text-sm">
                Vos clients apparaîtront ici après leur première commande.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredCustomers.map((customer) => {
              const badge = getCustomerBadge(customer);
              return (
                <Card 
                  key={customer.id} 
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => viewCustomerDetail(customer)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      {/* Avatar */}
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center flex-shrink-0">
                        <span className="text-lg font-bold text-gray-500">
                          {customer.name?.charAt(0).toUpperCase() || '?'}
                        </span>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-charcoal truncate">{customer.name}</h3>
                          {badge && (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
                              {badge.label}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{customer.email}</p>
                        {customer.phone && (
                          <p className="text-sm text-muted-foreground">{customer.phone}</p>
                        )}
                      </div>

                      {/* Stats */}
                      <div className="text-right flex-shrink-0">
                        <p className="font-bold text-charcoal">{formatCurrency(customer.total_spent)}</p>
                        <p className="text-xs text-muted-foreground">
                          {customer.orders_count} commande{customer.orders_count > 1 ? 's' : ''}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(customer.last_order_date)}
                        </p>
                      </div>

                      <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Customer Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
                <span className="text-xl font-bold text-amber-600">
                  {selectedCustomer?.name?.charAt(0).toUpperCase() || '?'}
                </span>
              </div>
              <div>
                <h2 className="text-lg font-bold">{selectedCustomer?.name}</h2>
                {getCustomerBadge(selectedCustomer) && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCustomerBadge(selectedCustomer).color}`}>
                    {getCustomerBadge(selectedCustomer).label}
                  </span>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>

          {selectedCustomer && (
            <div className="space-y-6 mt-4">
              {/* Contact Info */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">Contact</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">{selectedCustomer.email}</span>
                  </div>
                  {selectedCustomer.phone && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">{selectedCustomer.phone}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">Statistiques</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-700">{formatCurrency(selectedCustomer.total_spent)}</p>
                    <p className="text-xs text-green-600">Total dépensé</p>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-700">{selectedCustomer.orders_count}</p>
                    <p className="text-xs text-blue-600">Commandes</p>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-purple-700">
                      {formatCurrency((selectedCustomer.total_spent || 0) / Math.max(selectedCustomer.orders_count || 1, 1))}
                    </p>
                    <p className="text-xs text-purple-600">Panier moyen</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg text-center">
                    <p className="text-lg font-bold text-amber-700">{formatDate(selectedCustomer.first_order_date)}</p>
                    <p className="text-xs text-amber-600">Première commande</p>
                  </div>
                </div>
              </div>

              {/* Recent Orders */}
              {selectedCustomer.recent_orders && selectedCustomer.recent_orders.length > 0 && (
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
                    Dernières commandes
                  </h3>
                  <div className="space-y-2">
                    {selectedCustomer.recent_orders.map((order, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium">#{order.order_number}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(order.date)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">{formatCurrency(order.total)}</p>
                          <Badge variant={order.status === 'completed' ? 'default' : 'secondary'} className="text-xs">
                            {order.status === 'completed' ? 'Terminée' : order.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Favorite Products */}
              {selectedCustomer.favorite_products && selectedCustomer.favorite_products.length > 0 && (
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
                    Produits favoris
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedCustomer.favorite_products.map((product, idx) => (
                      <Badge key={idx} variant="outline" className="px-3 py-1">
                        {product.name} ({product.count}x)
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <Button variant="outline" className="flex-1" asChild>
                  <a href={`mailto:${selectedCustomer.email}`}>
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                  </a>
                </Button>
                {selectedCustomer.phone && (
                  <Button variant="outline" className="flex-1" asChild>
                    <a href={`tel:${selectedCustomer.phone}`}>
                      <Phone className="w-4 h-4 mr-2" />
                      Appeler
                    </a>
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
